import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DeviceCard from '../Grouping/DeviceCard';
import GroupHeader from '../Grouping/GroupHeader';
import { useHealth } from '../Health/HealthContext';
import { groupSummary } from '../../screenRowModel';
import { IcDown } from './icons';

const sameIds = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
const toggled = (set, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };

/** the pending (just dropped, not yet echoed by the server) order laid over the groups the page was given */
const withPending = (groups, pending) => {
  const keys = Object.keys(pending);
  if (!keys.length) return groups;
  const out = { ...groups };
  keys.forEach((k) => {
    if (!out[k]) return;
    const byId = new Map((out[k].items || []).map((d) => [d.id, d]));
    const items = pending[k].map((id) => byId.get(id)).filter(Boolean);
    // a screen that appeared meanwhile stays at the end; the page's data is never dropped
    (out[k].items || []).forEach((d) => { if (!pending[k].includes(d.id)) items.push(d); });
    out[k] = { ...out[k], items };
  });
  return out;
};

/**
 * Screens LIST view: groups (collapsible, with a status summary) of rows (screens-list-prototype). The order, the data and the refresh are the
 * page's -- this component only DRAWS the groups it is given in exactly that order, and keeps its own UI state (expanded rows, collapsed groups) so a
 * silent background refresh never closes a row or moves the scroll.
 *
 * groups:    { key: { group_id, name, items: [device] } } -- already filtered by the page's chip / search, in the saved order
 * allGroups: the same, unfiltered (the group header summary describes the whole group, not just what the filter shows)
 * locked:    a chip or the search hides screens -> dragging is off (an order saved from a partial list would be wrong)
 * onSaveOrder(ids): saves a group's new order through PUT /groups/devices/order
 * onSaveGroupOrder(ids): saves the GROUPS' order (group ids, every group) through PUT /groups/order -- the call the cards view's group drag / move up-down makes
 * assignSchedule(group): the cards header's «assign schedule» shortcut
 * The group header is the cards view's own GroupHeader (variant "list"): same ⋮ menu, rename form, confirm dialogs and mutations.
 */
export default function ScreensListView({ groups, allGroups, locked, onSaveOrder, onSaveGroupOrder, assignSchedule }) {
  const { t } = useTranslation();
  const { byId } = useHealth();
  const [expanded, setExpanded] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [dragFrom, setDragFrom] = useState(null);
  const [pending, setPending] = useState({});
  const [pendingGroups, setPendingGroups] = useState(null);   // the full group-key order just dropped, until the server echoes it
  const [reported, setReported] = useState({});
  const report = useCallback((id, v) => setReported((prev) => (prev[id] && prev[id].off === v.off && prev[id].warn === v.warn ? prev : { ...prev, [id]: v })), []);
  const timers = useRef({});

  // drop the pending order of a group once the server echoes it (or after a few seconds: a failed save snaps back to the server's order)
  useEffect(() => {
    setPending((prev) => {
      let changed = false; const next = { ...prev };
      Object.keys(prev).forEach((k) => {
        const ids = (groups[k]?.items || []).map((d) => d.id);
        if (!groups[k] || sameIds(ids, prev[k])) { delete next[k]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [groups]);
  useEffect(() => {
    if (!pendingGroups) return;
    const keys = Object.keys(allGroups || groups).filter((k) => (allGroups || groups)[k]);
    if (sameIds(keys, pendingGroups)) setPendingGroups(null);
  }, [groups, allGroups, pendingGroups]);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const shown = useMemo(() => withPending(groups, pending), [groups, pending]);
  // every group, in the order the page has them (or the one just dropped); the list draws only those that have screens to show
  const allKeys = useMemo(() => {
    const keys = Object.keys(allGroups || groups);
    if (!pendingGroups) return keys;
    return [...pendingGroups.filter((k) => keys.includes(k)), ...keys.filter((k) => !pendingGroups.includes(k))];
  }, [allGroups, groups, pendingGroups]);
  const visible = useMemo(() => allKeys.filter((k) => shown[k] && (shown[k].items || []).length > 0), [allKeys, shown]);

  const saveGroupOrder = useCallback((keys) => {
    setPendingGroups(keys);
    clearTimeout(timers.current.__groups);
    timers.current.__groups = setTimeout(() => setPendingGroups(null), 5000);
    onSaveGroupOrder?.(keys.map((k) => (allGroups || groups)[k]?.group_id).filter(Boolean));
  }, [allGroups, groups, onSaveGroupOrder]);
  // move one group to sit where another is (both among the groups on screen); groups with nothing to show keep their place
  const moveGroupTo = useCallback((fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const keys = allKeys.filter((k) => k !== fromKey);
    const at = keys.indexOf(toKey);
    const fromIdx = allKeys.indexOf(fromKey); const toIdx = allKeys.indexOf(toKey);
    keys.splice(fromIdx < toIdx ? at + 1 : at, 0, fromKey);
    saveGroupOrder(keys);
  }, [allKeys, saveGroupOrder]);
  const onMoveGroup = useCallback((groupId, direction) => {
    const fromKey = visible.find((k) => (allGroups || groups)[k]?.group_id === groupId);
    const i = visible.indexOf(fromKey); const j = direction === 'up' ? i - 1 : i + 1;
    if (!fromKey || j < 0 || j >= visible.length) return;
    moveGroupTo(fromKey, visible[j]);
  }, [visible, allGroups, groups, moveGroupTo]);

  const onDragEnd = useCallback((r) => {
    setDragFrom(null);
    if (r.type === 'GROUP') {
      if (!r.destination || r.destination.index === r.source.index) return;
      moveGroupTo(visible[r.source.index], visible[r.destination.index]);
      return;
    }
    if (!r.destination || r.source.droppableId !== r.destination.droppableId || r.source.index === r.destination.index) return;
    const key = r.source.droppableId;
    const ids = (shown[key]?.items || []).map((d) => d.id);
    const [moved] = ids.splice(r.source.index, 1);
    ids.splice(r.destination.index, 0, moved);
    setPending((p) => ({ ...p, [key]: ids }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => setPending((p) => { const n = { ...p }; delete n[key]; return n; }), 5000);
    onSaveOrder?.(ids);
  }, [shown, onSaveOrder, moveGroupTo, visible]);

  return (
    <div className="pp-ls-wrap" data-testid="screens-list">
      <DragDropContext onDragStart={(s0) => { if (s0.type !== 'GROUP') setDragFrom(s0.source.droppableId); }} onDragEnd={onDragEnd}>
        <Droppable droppableId="groups-root" type="GROUP">
          {(gp) => (
            <div ref={gp.innerRef} {...gp.droppableProps} className="pp-ls-groups">
              {visible.map((key, gi) => {
                const g = shown[key];
                const fullGroup = (allGroups || groups)[key] || g;
                const full = fullGroup.items || [];
                const sum = groupSummary(full, byId, reported);
                const col = collapsed.has(key);
                return (
                  <Draggable key={key} draggableId={key} index={gi} isDragDisabled={!!locked}>
                    {(pg, snapG) => (
                      <section
                        ref={pg.innerRef}
                        {...pg.draggableProps}
                        className={`pp-ls-grp${locked ? ' pp-ls-locked' : ''}${snapG.isDragging ? ' pp-ls-gdragging' : ''}`}
                        style={pg.draggableProps.style}
                        data-g={key}
                        data-collapsed={col}
                        data-testid="list-group"
                      >
                        <GroupHeader
                          variant="list"
                          groupInfo={fullGroup}
                          assignSchedule={assignSchedule || (() => {})}
                          onMoveGroup={onMoveGroup}
                          isFirst={gi === 0}
                          isLast={gi === visible.length - 1}
                          dragHandleProps={pg.dragHandleProps}
                          collapsed={col}
                          onToggleCollapse={() => setCollapsed((s0) => toggled(s0, key))}
                          orderLocked={!!locked}
                          summary={(
                            <span className="pp-ls-meta">
                              <span>{t('screens_list.group_count', { count: full.length })}</span>
                              {sum.off ? <span className="pp-ls-b">{t('screens_list.group_off', { count: sum.off })}</span> : null}
                              {sum.warn ? <span className="pp-ls-w">{t('screens_list.group_warn', { count: sum.warn })}</span> : null}
                              {!sum.off && !sum.warn ? <span className="pp-ls-g">{t('screens_list.group_ok')}</span> : null}
                            </span>
                          )}
                        />
                        <Droppable droppableId={key} type="DEVICE" isDropDisabled={dragFrom !== null && dragFrom !== key}>
                          {(prov) => (
                            <div ref={prov.innerRef} {...prov.droppableProps} className="pp-ls-grp-body pp-ls-list">
                              {(g.items || []).map((d, i) => (
                                <Draggable key={d.id} draggableId={d.id} index={i} isDragDisabled={!!locked}>
                                  {(p, snap) => (
                                    <div ref={p.innerRef} {...p.draggableProps} className="pp-ls-rowwrap" style={p.draggableProps.style}>
                                      <DeviceCard
                                        variant="row"
                                        data={d}
                                        rowUi={{ expanded: expanded.has(d.id), onToggle: () => setExpanded((s0) => toggled(s0, d.id)), handleProps: p.dragHandleProps, dragging: snap.isDragging, onReport: report }}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {prov.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </section>
                    )}
                  </Draggable>
                );
              })}
              {gp.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
